// Cooking mode: one step at a time, large type, screen kept awake.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link, navigate } from '../router';
import { Close, Next, Sun } from '../icons';
import { load } from '../storage';
import { ingredientsForStep } from '../lib/steps';
import type { ReelDetail } from '../types';
import { useT } from '../i18n';
import { getScript } from '../script';

export function Cook({ id }: { id: number }) {
  const t = useT();
  const [reel, setReel] = useState<ReelDetail | null>(() => load<ReelDetail | null>(`er.cache.reel.${id}`, null));
  const [err, setErr] = useState('');
  const [i, setI] = useState(0);
  const [lang, setLang] = useState<'en' | 'ta'>(() => (getScript() === 'ta' ? 'ta' : 'en'));
  const [awake, setAwake] = useState(false);

  useEffect(() => {
    api.reel(id).then(setReel).catch((e: ApiError) => { if (!reel && e.status !== 401) setErr(e.message); });
  }, [id]);

  // Keep the screen on while cooking; re-acquire after the tab becomes visible again.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock?.request('screen');
        setAwake(!!lock);
        lock?.addEventListener('release', () => setAwake(false));
      } catch { setAwake(false); }
    };
    const onVis = () => document.visibilityState === 'visible' && acquire();
    acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); lock?.release().catch(() => undefined); };
  }, []);

  const c = reel?.content;
  const stepsEN = c?.steps_en ?? [];
  const stepsTA = c?.steps_ta?.length === stepsEN.length ? c.steps_ta : [];
  const total = stepsEN.length;
  const last = total - 1;

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setI((n) => Math.min(last, n + 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1));
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [last]);

  if (err) return <div class="cook page" style="padding:24px"><p>{err}</p><Link href={`/r/${id}`}>{t('cook.backToRecipe')}</Link></div>;
  if (!c || total === 0) {
    return <div class="cook page" style="padding:24px"><p>{reel ? t('cook.noSteps') : t('common.loading')}</p><Link href={`/r/${id}`}>{t('cook.backToRecipe')}</Link></div>;
  }

  const ta = lang === 'ta' && stepsTA.length > 0;
  const text = ta ? stepsTA[i] : stepsEN[i];
  const need = ingredientsForStep(stepsEN[i], c.ingredients_en).map((k) => {
    const src = ta ? c.ingredients_ta[k] ?? c.ingredients_en[k] : c.ingredients_en[k];
    return src.quantity ? `${src.name} · ${src.quantity}` : src.name;
  });

  return (
    <div class="cook">
      <div class="page" style="padding:12px 16px calc(20px + env(safe-area-inset-bottom))">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
          <Link href={`/r/${id}`} style="min-height:44px;display:flex;align-items:center;gap:6px;font-weight:700;font-size:15px;text-decoration:none"><Close />{t('cook.exit')}</Link>
          <div style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--night-muted)" aria-live="polite">
            <Sun />{awake ? t('cook.awake') : t('cook.keepOn')}
          </div>
          {stepsTA.length > 0 && (
            <button onClick={() => setLang(lang === 'ta' ? 'en' : 'ta')} aria-label={t('cook.switch')}
              style="min-width:64px;height:44px;padding:0 12px;border-radius:12px;border:1px solid var(--night-card);background:var(--night-card);color:var(--on-night);font-weight:700;font-size:14px">
              {lang === 'ta' ? t('script.en') : t('script.ta')}
            </button>
          )}
        </div>

        <div class="bars" style={`margin-top:14px;grid-template-columns:repeat(${total},minmax(0,1fr))`} role="progressbar"
          aria-valuemin={1} aria-valuemax={total} aria-valuenow={i + 1} aria-label={t('cook.progress')}>
          {stepsEN.map((_, k) => <div class={'bar' + (k <= i ? ' on' : '')} />)}
        </div>

        <div style="margin-top:26px;font-size:15px;font-weight:700;letter-spacing:.04em;color:var(--night-accent)">
          {t('cook.step')} {i + 1} / {total}
        </div>
        <div lang={ta ? 'ta' : 'en'} class="display" style="margin-top:10px;font-weight:600;font-size:29px;line-height:1.45;text-wrap:pretty" aria-live="polite">{text}</div>

        {need.length > 0 && (
          <div style="margin-top:22px;display:flex;flex-direction:column;gap:8px">
            <div style="font-size:13px;color:var(--night-muted);font-weight:600">{t('cook.forStep')}</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px">
              {need.map((n) => <span lang={ta ? 'ta' : undefined} style="padding:6px 12px;border-radius:999px;background:var(--night-card);font-size:15px;font-weight:600">{n}</span>)}
            </div>
          </div>
        )}

        <div style="margin-top:auto;padding-top:24px;display:grid;grid-template-columns:1fr 2fr;gap:10px">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} aria-label={t('cook.prev')}
            style="height:72px;border-radius:20px;border:1.5px solid var(--night-muted);background:transparent;color:var(--on-night);font-weight:700;font-size:17px">{t('common.back')}</button>
          <button onClick={() => (i === last ? navigate(`/r/${id}#my-notes`) : setI(i + 1))}
            style="height:72px;border-radius:20px;border:0;background:var(--night-accent);color:var(--on-night-accent);font-weight:800;font-size:19px;display:flex;align-items:center;justify-content:center;gap:8px">
            {i === last ? t('cook.done') : t('cook.next')}<Next />
          </button>
        </div>
      </div>
    </div>
  );
}
